// Keep this file in sync with internal/topic/datetime.go.

import { isValid } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import type { Topic } from './types';

/**
 * Parses a stored ISO date-time string as a Date. An empty or missing value,
 * such as a news topic's end, reads as undefined; an unparseable one as an
 * Invalid Date.
 */
export function datetimeAsDateObj(time?: string): Date | undefined {
  return time ? new Date(time) : undefined;
}

/**
 * Midnight, in `tz`, of the day `date` falls on there. Undefined and an
 * Invalid Date come back as they are.
 */
export function startOfDayInZone(
  date: Date | undefined,
  tz: string
): Date | undefined {
  if (date === undefined || !isValid(date)) return date;
  return fromZonedTime(
    `${formatInTimeZone(date, tz, 'yyyy-MM-dd')}T00:00:00`,
    tz
  );
}

/**
 * Formats a date like "Friday, Apr 17, 2026" (iOS Calendar style) in the given
 * IANA zone. Undefined (no date) formats as ''; null and an Invalid Date as
 * "invalid".
 */
export function formatTopicDate(
  date: Date | null | undefined,
  tz: string
): string {
  if (date === undefined) return '';
  if (date === null || !isValid(date)) return 'invalid';
  return formatInTimeZone(date, tz, 'EEEE, MMM d, yyyy');
}

/**
 * Formats a time in iOS Calendar compact form in the given IANA zone:
 * "9 PM" on the hour, "9:30 PM" otherwise. Undefined (no date) formats as '';
 * null and an Invalid Date as "invalid".
 */
export function formatTopicTime(
  date: Date | null | undefined,
  tz: string
): string {
  if (date === undefined) return '';
  if (date === null || !isValid(date)) return 'invalid';
  const onTheHour = formatInTimeZone(date, tz, 'mm') === '00';
  return formatInTimeZone(date, tz, onTheHour ? 'h a' : 'h:mm a');
}

/**
 * Formats a date and time like "Friday, Apr 17, 2026 at 9 PM" in the given
 * IANA zone. Undefined (no date) formats as ''; null and an Invalid Date as
 * "invalid".
 */
export function formatTopicDatetime(
  date: Date | null | undefined,
  tz: string
): string {
  if (date === undefined) return '';
  if (date === null || !isValid(date)) return 'invalid';
  return `${formatTopicDate(date, tz)} at ${formatTopicTime(date, tz)}`;
}

/**
 * Formats a same-day topic range as two lines in the given IANA zone:
 *   ["Friday, Apr 17, 2026", "9 PM - 10 PM"]
 */
export function formatSameDayTopicRange(
  start: Date,
  end: Date,
  tz: string,
  includeTimezone = false
): string[] {
  const timeRange = `${formatTopicTime(start, tz)} - ${formatTopicTime(end, tz)}${
    includeTimezone ? ` (${compactTimezoneName(start, tz)})` : ''
  }`;
  return [formatTopicDate(start, tz), timeRange];
}

/**
 * Formats a topic with no end time, such as a news story, as two lines in the
 * given IANA zone: ["Friday, Apr 17, 2026", "9 PM"]
 */
export function formatTopicMoment(
  start: Date,
  tz: string,
  includeTimezone = false
): string[] {
  const time = `${formatTopicTime(start, tz)}${
    includeTimezone ? ` (${compactTimezoneName(start, tz)})` : ''
  }`;
  return [formatTopicDate(start, tz), time];
}

/**
 * Formats a multi-day topic range as two lines in the given IANA zone:
 *   ["Starts Friday, Apr 17, 2026 at 11 PM", "Ends Saturday, Apr 18, 2026 at 1 AM"]
 */
export function formatMultiDayTopicRange(
  start: Date,
  end: Date,
  tz: string,
  includeTimezone = false
): string[] {
  const startTimezone = includeTimezone
    ? ` (${compactTimezoneName(start, tz)})`
    : '';
  const endTimezone = includeTimezone
    ? ` (${compactTimezoneName(end, tz)})`
    : '';
  return [
    `Starts ${formatTopicDate(start, tz)} at ${formatTopicTime(start, tz)}${startTimezone}`,
    `Ends ${formatTopicDate(end, tz)} at ${formatTopicTime(end, tz)}${endTimezone}`,
  ];
}

/**
 * Returns the display lines for an topic's date/time range rendered in the
 * given IANA zone (defaults to the topic's authored zone). Compares calendar
 * day within that zone; dispatches to same-day vs multi-day helper.
 */
export function formatTopicDateTimeLines(
  topic: Topic,
  tz?: string,
  includeTimezone = false
): string[] {
  const zone = tz ?? topic.timezone;
  const start = datetimeAsDateObj(topic.startTime);
  const end = datetimeAsDateObj(topic.endTime);
  if (start === undefined) return [];
  if (end === undefined) {
    return formatTopicMoment(start, zone, includeTimezone);
  }
  const startDay = formatInTimeZone(start, zone, 'yyyy-MM-dd');
  const endDay = formatInTimeZone(end, zone, 'yyyy-MM-dd');
  if (startDay === endDay) {
    return formatSameDayTopicRange(start, end, zone, includeTimezone);
  }
  return formatMultiDayTopicRange(start, end, zone, includeTimezone);
}

function compactTimezoneName(date: Date, tz: string): string {
  return formatInTimeZone(date, tz, 'zzz');
}
